import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { UserCheck, Clock, CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ReviewAction = 'approve' | 'reject' | 'request_changes' | 'custom'
export type HumanReviewStatus = 'idle' | 'waiting' | 'approved' | 'rejected' | 'timeout' | 'error'

export interface HumanReviewShapeNodeData {
  label: string
  reviewTitle?: string
  assigneeName?: string
  assigneeType?: 'user' | 'role' | 'group'
  allowedActions?: ReviewAction[]
  status?: HumanReviewStatus
  onDelete?: () => void
  [key: string]: unknown
}

const statusConfig: Record<HumanReviewStatus, { color: string; icon: typeof UserCheck; animate?: boolean }> = {
  idle: { color: '#06b6d4', icon: UserCheck },
  waiting: { color: '#f59e0b', icon: Clock, animate: true },
  approved: { color: '#10b981', icon: CheckCircle2 },
  rejected: { color: '#ef4444', icon: XCircle },
  timeout: { color: '#f97316', icon: AlertTriangle },
  error: { color: '#ef4444', icon: AlertTriangle },
}

const actionBadgeColors: Record<ReviewAction, string> = {
  approve: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  reject: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
  request_changes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  custom: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
}

const actionLabels: Record<ReviewAction, string> = {
  approve: 'Approve',
  reject: 'Reject',
  request_changes: 'Changes',
  custom: 'Custom',
}

interface HumanReviewShapeNodeProps {
  data: HumanReviewShapeNodeData
  selected?: boolean
}

function HumanReviewShapeNodeComponent({ data, selected }: HumanReviewShapeNodeProps) {
  const {
    label,
    reviewTitle,
    assigneeName,
    allowedActions = [],
    status = 'idle',
  } = data

  const config = statusConfig[status]
  const StatusIcon = config.icon
  const displayTitle = reviewTitle || label || 'Human Review'

  return (
    <div
      className="relative group"
      style={{ width: 200, height: 95 }}
    >
      {/* Document/manual input shape - rectangle with folded corner */}
      <svg
        viewBox="0 0 200 95"
        className="absolute inset-0 w-full h-full"
        style={{
          filter: selected
            ? `drop-shadow(0 0 12px ${config.color}40)`
            : 'drop-shadow(0 4px 6px rgba(0,0,0,0.07))',
        }}
      >
        <defs>
          <linearGradient id={`review-grad-${status}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={config.color} stopOpacity="0.15" />
            <stop offset="100%" stopColor={config.color} stopOpacity="0.05" />
          </linearGradient>
          <clipPath id="review-clip">
            <path d="M 12 3 L 170 3 L 197 30 L 197 82 Q 197 92 187 92 L 12 92 Q 3 92 3 82 L 3 12 Q 3 3 12 3 Z" />
          </clipPath>
        </defs>
        {/* Main shape with folded corner */}
        <path
          d="M 12 3 L 170 3 L 197 30 L 197 82 Q 197 92 187 92 L 12 92 Q 3 92 3 82 L 3 12 Q 3 3 12 3 Z"
          fill={`url(#review-grad-${status})`}
          stroke={selected ? config.color : '#e5e7eb'}
          strokeWidth={selected ? 2.5 : 1.5}
          className="dark:stroke-gray-600"
        />
        {/* Folded corner */}
        <path
          d="M 170 3 L 170 24 Q 170 30 176 30 L 197 30"
          fill="none"
          stroke={selected ? config.color : '#e5e7eb'}
          strokeWidth={selected ? 2.5 : 1.5}
          className="dark:stroke-gray-600"
        />
        <path
          d="M 170 3 L 170 24 Q 170 30 176 30 L 197 30 L 170 3 Z"
          fill={`${config.color}20`}
        />
        {/* Top accent line */}
        <line
          x1="12"
          y1="3"
          x2="170"
          y2="3"
          stroke={config.color}
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>

      {/* Content */}
      <div className="absolute inset-0 flex flex-col p-3 pt-4">
        {/* Header row */}
        <div className="flex items-start gap-2.5">
          {/* Icon with status */}
          <div className="relative flex-shrink-0">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <UserCheck className="w-4 h-4" style={{ color: config.color }} />
            </div>
            {status !== 'idle' && (
              <div className="absolute -bottom-0.5 -right-0.5 p-0.5 bg-white dark:bg-gray-800 rounded-full">
                <StatusIcon
                  className={cn('w-3 h-3', config.animate && 'animate-spin')}
                  style={{ color: config.color }}
                />
              </div>
            )}
          </div>

          {/* Text */}
          <div className="min-w-0 flex-1 pr-4">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {displayTitle}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {assigneeName || (status === 'waiting' ? 'Waiting for review...' : 'Assign reviewer')}
            </p>
          </div>
        </div>

        {/* Action badges */}
        {allowedActions.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1">
            {allowedActions.slice(0, 3).map((action) => (
              <span
                key={action}
                className={cn(
                  'inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium',
                  actionBadgeColors[action]
                )}
              >
                {actionLabels[action]}
              </span>
            ))}
            {allowedActions.length > 3 && (
              <span className="text-[10px] text-gray-400 px-1">
                +{allowedActions.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Waiting indicator */}
        {status === 'waiting' && (
          <div className="absolute bottom-2 right-2">
            <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
          </div>
        )}
      </div>

      {/* Target Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
        style={{ backgroundColor: config.color }}
      />

      {/* Source Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
        style={{ backgroundColor: config.color }}
      />
    </div>
  )
}

export const HumanReviewShapeNode = memo(HumanReviewShapeNodeComponent)
export default HumanReviewShapeNode
